import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  signal,
  ViewChildren,
  WritableSignal,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterModule } from '@angular/router';
import { VariationValue } from '@harnessio/ff-javascript-client-sdk/dist/types';
import { DsInputComponent, DsInputModule, DsModalVariant, DsModalService } from '@levelaccess/design-system';
import { FeatureFlagCollection } from '@shared/interfaces/feature-flag.interface';
import { Subject, Subscription } from 'rxjs';
import { skip, take } from 'rxjs/operators';

import { Api } from '@shared/constants/api';
import { $workspace, CHAR_LIMITS } from '@shared/constants/workspace';
import { IGlobalError } from '@shared/interfaces/error.interface';
import { IWorkspace, IWorkspaceUpdateRequest } from '@shared/interfaces/workspace.interface';
import { DigitalPropertyWorkspaceFilterComponent } from '../../../../components/digital-property-workspace-filter/digital-property-workspace-filter.component';
import { DsConfirmationModalComponent } from '../../../../components/ds-confirmation-modal/ds-confirmation-modal.component';
import { NotificationPosition } from '../../../../models/notification.model';
import { A11yService } from '../../../../services/a11y.service';
import { BusMessageChannels, BusMessageService } from '../../../../services/bus-message.service';
import { ErrorMessageService } from '../../../../services/error-message.service';
import { FeatureFlagService } from '../../../../services/feature-flag/feature-flag.service';
import { CustomValidators } from '../../../../services/helpers/form-custom-validators';
import { NotificationService } from '../../../../services/notification.service';
import { StandardizedHeaderBarComponent } from '../../../../services/standardized-header-bar-aware.component';
import { ICurrentSelectedWorkspace, UserService } from '../../../../services/user.service';
import { WorkspaceService } from '../../../../services/workspace.service';
import { SharedModule } from '../../../../shared/shared.module';
import { TranslateService } from '../../../../translate/translate.service';

@Component({
  imports: [CommonModule, ReactiveFormsModule, RouterModule, SharedModule, DsInputModule],
  selector: 'app-edit-workspace',
  templateUrl: './edit-workspace.component.html',
  styleUrls: ['./edit-workspace.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class EditWorkspaceComponent extends StandardizedHeaderBarComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription;

  @ViewChildren(DsInputComponent) private readonly dsInputs: QueryList<DsInputComponent>;

  @Input() readonly workspaceId: string;
  private resolvedWorkspaceId: string;
  @Input() readonly workspaceName: string;
  @Input() readonly workspaceDescription: string;
  @Input() readonly isModalMode: boolean = false;
  @Input() readonly inTabGroup: boolean = false;

  @Output() readonly workspaceSaved: EventEmitter<IWorkspace> = new EventEmitter<IWorkspace>();
  @Output() readonly cancelled: EventEmitter<void> = new EventEmitter<void>();

  public formValidationRequest: Subject<void>;
  public Api: typeof Api;
  public form: UntypedFormGroup;
  public saveError: WritableSignal<IGlobalError> = signal<IGlobalError>(null);
  public $workspace: typeof $workspace;
  public selectedWorkspace: ICurrentSelectedWorkspace;
  public CHAR_LIMITS: typeof CHAR_LIMITS;

  constructor(
    private element: ElementRef<HTMLElement>,
    private userService: UserService,
    private workspaceService: WorkspaceService,
    private busMessageService: BusMessageService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private errorMessageService: ErrorMessageService,
    private a11yService: A11yService,
    private formBuilder: UntypedFormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private dsModalService: DsModalService,
    @Optional() private activatedRoute?: ActivatedRoute,
    @Optional() private router?: Router,
    @Optional() private featureFlagService?: FeatureFlagService,
  ) {
    super();
    this.subscriptions = new Subscription();
    this.formValidationRequest = new Subject<void>();
    this.Api = Api;
    this.form = this.formBuilder.group({});
    this.$workspace = $workspace;
    this.CHAR_LIMITS = CHAR_LIMITS;

    this.createForm();
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      [$workspace.name]: this.formBuilder.control('', [
        CustomValidators.required,
        CustomValidators.validateIsEmpty,
        Validators.maxLength(this.CHAR_LIMITS[$workspace.name]),
      ]),
      [$workspace.description]: this.formBuilder.control('', [
        CustomValidators.required,
        CustomValidators.validateIsEmpty,
        Validators.maxLength(this.CHAR_LIMITS[$workspace.description]),
      ]),
    });
  }

  private setSelectedWorkspace(workspace: ICurrentSelectedWorkspace): void {
    this.selectedWorkspace = workspace;

    this.form.get($workspace.name).setValue(this.selectedWorkspace.workspaceName);
    this.form.get($workspace.description).setValue(this.selectedWorkspace.workspaceDescription);
  }

  private onUserWorkspace(data: ICurrentSelectedWorkspace): void {
    this.setSelectedWorkspace(data);

    this.changeDetectorRef.detectChanges();
    this.dsInputs?.forEach((input: DsInputComponent) => input.triggerHandlers());
  }

  private handleError(message: string, response: HttpErrorResponse): void {
    const errorMessage: string = this.errorMessageService.getGlobalErrorResponse(response);
    const appMessage: string = this.translateService.instant(message);

    this.a11yService.setMessage(appMessage + ': ' + errorMessage);

    console.error('[EditWorkspaceComponent.handleError: ' + appMessage + ']', response, errorMessage);

    this.saveError.set(response.error);

    this.changeDetectorRef.detectChanges();
  }

  private onEditWorkspaceError(response: HttpErrorResponse): void {
    this.handleError('error_while_saving_workspace', response);
  }

  private onEditWorkspaceSuccess(workspace: IWorkspace): void {
    this.userService.reloadUser();
    this.busMessageService.to(BusMessageChannels.workspaceUpdated).next();

    this.notificationService.success(
      this.translateService.instant('updated_selected_workspace'),
      NotificationPosition.Toast,
      true,
    );

    if (this.isModalMode) {
      this.workspaceSaved.emit(workspace);
    } else if (!this.inTabGroup && this.router) {
      void this.router.navigate(['/', Api.portfolio]);
    }
  }

  protected isFieldInvalid(field: string): boolean {
    const control = this.form?.get(field);
    return Boolean(control?.invalid && control?.touched);
  }

  public onSave(): void {
    this.formValidationRequest.next();
    this.form.markAllAsTouched();

    if (this.form.valid === false) {
      this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);
      return;
    }

    this.saveError.set(null);
    this.saveWorkspace();
  }

  public saveWorkspace(): void {
    const data: IWorkspaceUpdateRequest = {
      [$workspace.name]: this.form.get($workspace.name).value,
      [$workspace.description]: this.form.get($workspace.description).value,
    };
    const workspaceId: string = this.isModalMode ? this.resolvedWorkspaceId : this.selectedWorkspace.workspaceId;

    this.subscriptions.add(
      this.workspaceService.updateWorkspace(workspaceId, data).subscribe({
        next: this.onEditWorkspaceSuccess.bind(this),
        error: this.onEditWorkspaceError.bind(this),
      }),
    );
  }

  public ngOnInit(): void {
    this.resolvedWorkspaceId = this.workspaceId;

    if (this.isModalMode) {
      this.form.patchValue({
        [$workspace.name]: this.workspaceName ?? '',
        [$workspace.description]: this.workspaceDescription ?? '',
      });
      this.changeDetectorRef.detectChanges();
      return;
    }

    if (this.resolvedWorkspaceId) {
      this.subscriptions.add(
        this.userService.userWorkspace(this.resolvedWorkspaceId).subscribe({ next: this.onUserWorkspace.bind(this) }),
      );
    } else if (this.activatedRoute) {
      this.subscriptions.add(
        this.activatedRoute.paramMap.subscribe((params: ParamMap) => {
          this.resolvedWorkspaceId = params.get(Api.workspaceId);
          this.subscriptions.add(
            this.userService.userWorkspace(this.resolvedWorkspaceId).subscribe({ next: this.onUserWorkspace.bind(this) }),
          );
        }),
      );
    }

    this.subscriptions.add(
      this.form.valueChanges.subscribe(() => {
        if (this.form.dirty && !this.isModalMode) {
          DigitalPropertyWorkspaceFilterComponent.setOnChangeConfirmation(
            this,
            'switch_digital_property_from_portfolio_confirmation_text',
          );
        }
      }),
    );

    if (!this.isModalMode) {
      this.subscriptions.add(
        this.busMessageService
          .from(BusMessageChannels.userSwitchedToDigitalProperty)
          .pipe(skip(1))
          .subscribe(() => {
            void this.router?.navigate(['/', Api.dashboard]);
          }),
      );

      if (!this.inTabGroup && this.featureFlagService) {
        this.subscriptions.add(
          this.featureFlagService
            .variation$(FeatureFlagCollection.consolidateSettings, false)
            .pipe(skip(1))
            .subscribe((isEnabled: VariationValue) => {
              if (isEnabled) {
                void this.router?.navigate(['/', Api.overview, Api.workspaceSettings], {
                  queryParams: this.activatedRoute?.snapshot.queryParams,
                });
              }
            }),
        );
      }
    }
  }

  public onCancel(): void {
    if (this.isModalMode) {
      this.cancelled.emit();
    } else if (this.router) {
      void this.router.navigate(['/', Api.portfolio]);
    }
  }

  public onDiscardChanges(): void {
    const modalRef = this.dsModalService.open(DsConfirmationModalComponent, DsModalVariant.center)!;
    const modalComponent = modalRef.componentInstance as DsConfirmationModalComponent;

    modalComponent.title = this.translateService.instant('discard_changes_confirmation_title');
    modalComponent.description = this.translateService.instant('discard_changes_confirmation_description');
    modalComponent.confirmLabel = this.translateService.instant('discard_changes');
    modalComponent.cancelLabel = this.translateService.instant('keep_editing');
    modalComponent.maxWidthPercentage = 40;
    modalComponent.closeAllModals = false;

    this.subscriptions.add(modalComponent.confirm.pipe(take(1)).subscribe(() => this.discardChanges()));
  }

  private discardChanges(): void {
    this.setSelectedWorkspace(this.selectedWorkspace);
    this.form.markAsPristine();
    this.form.markAsUntouched();

    this.notificationService.success(
      this.translateService.instant('changes_discarded_successfully'),
      NotificationPosition.Toast,
      true,
    );
  }

  public ngOnDestroy(): void {
    if (!this.isModalMode) {
      DigitalPropertyWorkspaceFilterComponent.setOnChangeConfirmation(this);
    }
    this.subscriptions.unsubscribe();
  }
}
